import workspaceCss from "@/ui/workspace/WorkspacePage.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Sidebar} from "./Sidebar"
import {TerminableOwner, Terminator} from "@opendaw/lib-std"
import {Html} from "@opendaw/lib-dom"
import {StudioService} from "@/service/StudioService"
import {QuickCreatePage} from "@/ui/pages/QuickCreatePage"
import {ExplorePage} from "@/ui/pages/ExplorePage"
import {LibraryPage} from "@/ui/pages/LibraryPage"
import {Dashboard} from "@/ui/dashboard/Dashboard"
import {WorkspaceBuilder} from "@/ui/workspace/WorkspaceBuilder"

const workspaceClassName = Html.adoptStyleSheet(workspaceCss, "WorkspacePage")

type PageRenderer = (service: StudioService, lifecycle: TerminableOwner) => HTMLElement

const SIDEBAR_PAGES: Record<string, {renderer: PageRenderer, defaultCollapsed: boolean}> = {
    "/": {
        renderer: (service, lifecycle) => Dashboard({lifecycle, service}) as unknown as HTMLElement,
        defaultCollapsed: true
    },
    "/create": {
        renderer: (service, lifecycle) => QuickCreatePage({service, lifecycle, path: "/create", error: ""}) as unknown as HTMLElement,
        defaultCollapsed: false
    },
    "/explore": {
        renderer: (service, lifecycle) => ExplorePage({service, lifecycle, path: "/explore", error: ""}) as unknown as HTMLElement,
        defaultCollapsed: false
    },
    "/library": {
        renderer: (service, lifecycle) => LibraryPage({service, lifecycle, path: "/library", error: ""}) as unknown as HTMLElement,
        defaultCollapsed: false
    },
    "/studio": {
        renderer: () => null as unknown as HTMLElement,
        defaultCollapsed: true
    }
}

export const SidebarLayout = ({service, lifecycle, path}: {service: StudioService, lifecycle: TerminableOwner, path: string}) => {
    const config = SIDEBAR_PAGES[path]
    if (!config) return <div>404</div>

    const contentLifetime = new Terminator()
    lifecycle.own(contentLifetime)

    const contentContainer: HTMLDivElement = (
        <div style={{flex: "1", display: "flex", flexDirection: "column", minWidth: "0", height: "100%", overflow: "hidden"}}/>
    ) as unknown as HTMLDivElement

    const sidebar = Sidebar({defaultCollapsed: config.defaultCollapsed}) as unknown as HTMLElement

    const wrapper: HTMLDivElement = (
        <div style={{display: "flex", height: "100%", width: "100%", overflow: "hidden"}}>
            {sidebar}
            {contentContainer}
        </div>
    ) as unknown as HTMLDivElement

    if (path === "/studio") {
        const screenLifeTime = lifecycle.own(new Terminator())
        const main: HTMLElement = <main/> as unknown as HTMLElement
        const workspaceDiv: HTMLDivElement = <div className={workspaceClassName}>{main}</div> as unknown as HTMLDivElement
        contentContainer.appendChild(workspaceDiv)

        lifecycle.own(service.layout.screen.catchupAndSubscribe(owner => {
            screenLifeTime.terminate()
            const screenKey = owner.getValue()
            WorkspaceBuilder.buildScreen(screenLifeTime, service.panelLayout, main, screenKey)

            const showSidebar = screenKey === "dashboard" || screenKey === null
            sidebar.style.display = showSidebar ? "" : "none"
        }))
    } else {
        const pageEl = config.renderer(service, contentLifetime)
        contentContainer.appendChild(pageEl as unknown as Node)
    }

    return wrapper as unknown as HTMLElement
}
